import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import QS from 'qs'

import { useFlags } from 'flags'

import Button from '../Button'

import './GoogeAuthButton.scss'
import Icon from '../Icon'

interface GoogleSignInButtonProps {
  type: 'signIn' | 'signUp'
  onClick: (credential: string) => void
}

interface GoogleCredentialResponse {
  clientId: string
  client_id: string
  credential: string
  select_by: string
}

interface GoogleIdConfig {
  client_id: string
  callback: (response: GoogleCredentialResponse) => void
}

interface GoogleId {
  initialize: (config: GoogleIdConfig) => void
  renderButton: (
    parent: HTMLElement,
    options: {
      type?: 'standard' | 'icon'
      theme?: 'outline' | 'filled_blue' | 'filled_black'
      size?: 'small' | 'medium' | 'large'
      text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin'
      shape?: 'rectangular' | 'pill' | 'circle' | 'square'
      logo_alignment?: 'left' | 'center'
      width?: string | number
    }
  ) => void
  disableAutoSelect: () => void
  cancel: () => void
}

interface GoogleAccounts {
  id: GoogleId
}

interface GoogleGlobal {
  accounts: GoogleAccounts
}

declare global {
  interface Window {
    google?: GoogleGlobal
  }
}

/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types */
export function GoogleAuthButton({ type, onClick }: GoogleSignInButtonProps) {
  const flags = useFlags()
  const location = useLocation()
  const queryParams = QS.parse(location.search, { ignoreQueryPrefix: true })

  const buttonRef = useRef<HTMLDivElement>(null)

  const onClickRef = useRef(onClick)
  onClickRef.current = onClick

  const { t } = useTranslation()

  useEffect(() => {
    const SCRIPT_SRC = 'https://accounts.google.com/gsi/client'

    const ensureInit = () => {
      if (!window.google?.accounts?.id) return

      window.google.accounts.id.initialize({
        client_id: process.env['REACT_APP_GOOGLE_CLIENT_ID'] as string,
        callback: (response: {
          clientId: string
          client_id: string
          credential: string
          select_by: string
        }) => {
          onClickRef.current?.(response.credential)
        },
      })

      window.google.accounts.id.disableAutoSelect()

      if (buttonRef.current) {
        buttonRef.current.innerHTML = ''
        window.google.accounts.id.renderButton(buttonRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: type === 'signIn' ? 'signin_with' : 'signup_with',
          shape: 'rectangular',
          width: '100%',
        })
      }
    }

    let script = document.querySelector<HTMLScriptElement>(
      `script[src="${SCRIPT_SRC}"]`
    )
    const handleLoad = () => ensureInit()

    if (window.google?.accounts?.id) {
      ensureInit()
    } else if (script) {
      script.addEventListener('load', handleLoad, { once: true })
    } else {
      script = document.createElement('script')
      script.src = SCRIPT_SRC
      script.async = true
      script.defer = true
      script.addEventListener('load', handleLoad, { once: true })
      document.body.appendChild(script)
    }

    return () => {
      script?.removeEventListener('load', handleLoad)
      try {
        window.google?.accounts?.id?.cancel()
        window.google?.accounts?.id?.disableAutoSelect()
      } catch {
        // ignore
      }

      if (buttonRef.current) {
        buttonRef.current.innerHTML = ''
      }
    }
  }, [type])

  if (!flags['allowGoogleSsoLogin'] || !queryParams['enableSso']) {
    return null
  }

  return (
    <div className="root">
      <div ref={buttonRef} role="button" className="hidden-button" />
      <Button
        square
        secondary
        large
        className="main-button"
        onClick={(e: Event) => {
          const googleBtn = buttonRef.current?.querySelector<HTMLButtonElement>(
            "button, [role='button']"
          )

          if (googleBtn) {
            googleBtn.click()
            e.preventDefault()
          }
        }}
      >
        <Icon type="google-g-logo" />
        {type === 'signIn'
          ? t('Sign In with Google')
          : t('Sign Up with Google')}
      </Button>
    </div>
  )
}
